import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "@/assets/css/style.css";
import "./AddFaculty.module.css";
import { useAppSelector } from "@/store/hooks";
import { set } from "date-fns";
import toast from "react-hot-toast";

const FacultyForm: React.FC = () => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;

    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [image, setImage] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>("");

    // ✅ Form fields
    // const [channel, setChannel] = useState("");
    const [FacultyName, setFacultyName] = useState("");
    const [FacultyPost, setFacultyPost] = useState("");
    const [FacultyExperience, setFacultyExperience] = useState("");

    //
    // const [link, setLink] = useState("");

    // ✅ Validation errors
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);

    const { token } = useAppSelector((state) => state.auth);

    // ✅ Fetch existing data if editing
    useEffect(() => {
        if (id) {
            fetch(`${baseUrl}/our-faculty/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            })

                .then((res) => res.json())
                .then((data) => {
                    if (data.success && data.data) {
                        const faculty = data.data; // use inside for clarity
                        setFacultyPost(faculty.post || "");
                        setFacultyExperience(faculty.experience || "");
                        setFacultyName(faculty.name || "");
                        if (faculty.photo) setPreviewUrl(faculty.photo);
                    }

                });
        }
    }, [id, token]);

    // ✅ Preview image
    useEffect(() => {
        if (image) {
            const url = URL.createObjectURL(image);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [image]);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) setImage(e.target.files[0]);
    };

    // ✅ Validation function
    const validate = () => {
        const newErrors: { [key: string]: string } = {};

        // Faculty Name
        if (!FacultyName?.trim()) {
            newErrors.facultyName = "Faculty Name is required";
        }

        // Faculty Post
        if (!FacultyPost?.trim()) {
            newErrors.facultyPost = "Faculty Post is required";
        }

        // Faculty Experience
        const experienceStr = String(FacultyExperience || "").trim();
        if (!experienceStr) {
            newErrors.facultyExperience = "Faculty Experience is required";
        } 
        // else if (isNaN(Number(experienceStr))) {
        //     newErrors.facultyExperience = "Experience must be a number";
        // } else if (Number(experienceStr) < 0) {
        //     newErrors.facultyExperience = "Experience cannot be negative";
        // }

        // Faculty Photo (only check if new entry, not editing)
        if (!id && !image) {
            newErrors.facultyPhoto = "Faculty Photo is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };



    // ✅ Submit handler
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const formData = new FormData();
            if (image) formData.append("photo", image);
            formData.append("name", FacultyName);
            formData.append("post", FacultyPost);
            formData.append("experience", FacultyExperience);

            const url = id
                ? `${baseUrl}/our-faculty/${id}`
                : `${baseUrl}/our-faculty/add_faculty`;

            const method = id ? "PUT" : "POST";

            const res = await fetch(url, {
                method,
                body: formData,
                headers: { Authorization: `Bearer ${token}` },
            });

            const data = await res.json();
            if (data) {
                // alert(id ? "Youtube Channel updated!" : "Youtube Channel added!");
                toast.success(id ? "Faculty updated!" : "Faculty added!", {
                    position: "top-right",
                    autoClose: 2000,
                }); 
                navigate("/our-faculty");
            } else {
                alert(data.message || "Something went wrong!");
            }
        } finally {
            setLoading(false);
        }
    };

    console.log("error", { errors });

    return (
        <div className="side-app">
            <div className="main-container container-fluid">
                {/* PAGE HEADER */}
                <div className="page-header">
                    <h1 className="page-title">
                        {id ? "Edit Faculty" : "Add Faculty"}
                    </h1>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><a href="/">Home</a></li>
                        <li className="breadcrumb-item active">Our Faculty</li>
                    </ol>
                </div>

                {/* FORM */}
                <div className="row">
                    <div className="col-xl-12 col-md-12">
                        <form className="card" onSubmit={handleSubmit}>
                            <div className="card-body">

                                <div className="form-group">
                                    <label className="form-label">Faculty Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={FacultyName}
                                        onChange={(e) => setFacultyName(e.target.value)}
                                    />
                                    {errors.FacultyName && <small className="text-danger">{errors.FacultyName}</small>}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Faculty Post</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={FacultyPost}
                                        onChange={(e) => setFacultyPost(e.target.value)}
                                    />
                                    {errors.FacultyPost && <small className="text-danger">{errors.FacultyPost}</small>}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Faculty Experience</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={FacultyExperience}
                                        onChange={(e) => setFacultyExperience(e.target.value)}
                                    />
                                    {errors.FacultyExperience && <small className="text-danger">{errors.FacultyExperience}</small>}
                                </div>
                                {/* Upload Box */}
                                <label className="form-label">Faculty Photo</label>

                                <div
                                    className="upload-box mb-4"
                                    onClick={() => document.getElementById("fileInput")?.click()}
                                    style={{
                                        border: "2px dashed #bbb",
                                        borderRadius: "12px",
                                        padding: "30px",
                                        textAlign: "center",
                                        cursor: "pointer",
                                        background: "#fafafa",
                                    }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        if (e.dataTransfer.files[0]) {
                                            setImage(e.dataTransfer.files[0]);
                                        }
                                    }}
                                >
                                    {previewUrl ? (
                                        <div>
                                            <img
                                                src={previewUrl}
                                                alt="Preview"
                                                className="img-fluid rounded shadow"
                                                style={{ maxHeight: "200px", objectFit: "cover" }}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-danger mt-3"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setImage(null);
                                                    setPreviewUrl("");
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <p className="fw-bold mb-1">Drag & Drop your image here</p>
                                            <p className="text-muted mb-0">or click to browse</p>
                                            <small className="d-block mt-2">
                                                Recommended: 180px * 180px
                                            </small>
                                            {errors.image && (
                                                <small className="text-danger">{errors.image}</small>
                                            )}
                                        </>
                                    )}
                                    <input
                                        id="fileInput"
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handleFileChange}
                                    />
                                </div>

                                {/* Submit */}
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    {loading ? (id ? "Updating…" : "Uploading…") : id ? "Update" : "Submit"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FacultyForm;
